import { Component, computed, input, output, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Card } from './card';
import { formatAge } from './format-age';
import { AttentionItem, OverviewSource, SummaryPart } from './overview-model';
import { Tone, toneClass } from './tone';

// More than this many things in the list is a different problem from the one the list solves.
const MAX_ITEMS = 8;

// What each source shows, and what is wrong right now across all of them.
// A new source (such as the clusters) is one more entry in `sources`; nothing here is specific to a source.
@Component({
  selector: 'app-overview',
  imports: [Card, DecimalPipe, RouterLink],
  templateUrl: './overview.html',
  styleUrl: './overview.css',
})
export class Overview {
  readonly sources = input.required<OverviewSource[]>();
  readonly attention = input.required<AttentionItem[]>();
  readonly now = input(new Date());

  // The reader is about to look at it: the details page is told what to search for.
  readonly open = output<{ route: string; search: string }>();

  protected readonly expanded = signal(false);
  protected readonly shownItems = computed(() =>
    this.expanded() ? this.attention() : this.attention().slice(0, MAX_ITEMS),
  );
  protected readonly hiddenCount = computed(
    () => this.attention().length - this.shownItems().length,
  );
  protected readonly anyReady = computed(() =>
    this.sources().some((source) => source.state === 'ready'),
  );

  protected toneClass(tone: Tone): string {
    return toneClass(tone);
  }

  // Short enough for a badge; the sentence goes in the body.
  protected badge(source: OverviewSource): string {
    switch (source.state) {
      case 'loading':
        return 'Loading';
      case 'empty':
        return 'Not configured';
      case 'unavailable':
        return 'No data';
      default:
        return source.tone === 'bad'
          ? 'Needs attention'
          : source.tone === 'warn'
            ? 'Worth a look'
            : 'Healthy';
    }
  }

  // The message counts are not a share of the things watched, so they stay out of the bar.
  protected segments(source: OverviewSource): SummaryPart[] {
    return source.parts.filter((part) => part.key !== 'deadMessages');
  }

  protected age(updated: Date): string {
    return formatAge(updated, this.now());
  }
}
